import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, Timestamp, query, where, getDocs } from 'firebase/firestore';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    fileUrl: '',
  });
  const [fileError, setFileError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'fileUrl') {
      setFileError('');
    }
  };

  function isValidUrl(url) {
    if (!url) return true;
    try {
      const parsed = new URL(url);
      const allowedHosts = [
        'wetransfer.com',
        'we.tl',
        'drive.google.com',
        'dropbox.com',
        'onedrive.live.com',
      ];
      return allowedHosts.some((host) => parsed.hostname.includes(host));
    } catch {
      return false;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (formData.fileUrl && !isValidUrl(formData.fileUrl)) {
      setFileError('Vul een geldige link in (WeTransfer, Google Drive, etc.) of laat het veld leeg.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'contactForms'), {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        fileUrl: formData.fileUrl,
        created: Timestamp.now(),
      });
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '', fileUrl: '' });
    } catch (err) {
      setError('Er is iets misgegaan: ' + (err && err.message ? err.message : JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {limitReached && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center font-semibold">
          Je hebt het maximum van 5 aanvragen per dag bereikt. Probeer het morgen opnieuw.
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6" style={limitReached ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">Naam *</label>
          <input type="text" name="name" id="name" required value={formData.name} onChange={handleChange} className="input w-full" />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
          <input type="email" name="email" id="email" required value={formData.email} onChange={handleChange} className="input w-full" />
        </div>
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700">Onderwerp *</label>
          <input type="text" name="subject" id="subject" required value={formData.subject} onChange={handleChange} className="input w-full" />
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700">Bericht *</label>
          <textarea name="message" id="message" rows={4} required value={formData.message} onChange={handleChange} className="input w-full" />
        </div>
        <div>
          <label htmlFor="fileUrl" className="block text-sm font-medium text-gray-700">WeTransfer/Drive link (optioneel)</label>
          <div className="flex items-center space-x-4 mt-2 mb-2">
            <a href="https://wetransfer.com/upload" target="_blank" rel="noopener noreferrer" title="Upload via WeTransfer" className="hover:scale-110 transition-transform">
              {/* WeTransfer SVG */}
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="#00BFFF"/><text x="16" y="21" textAnchor="middle" fontSize="16" fill="white" fontFamily="Arial" fontWeight="bold" dominantBaseline="middle">W</text></svg>
            </a>
            <a href="https://drive.google.com/drive/my-drive" target="_blank" rel="noopener noreferrer" title="Upload via Google Drive" className="hover:scale-110 transition-transform">
              {/* Google Drive SVG */}
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="8" fill="#fff"/>
                <polygon points="16,7 25,23 7,23" fill="#4285F4"/>
                <polygon points="16,7 25,23 21,23 12,7" fill="#34A853"/>
                <polygon points="7,23 16,7 12,7 7,23" fill="#FBBC05"/>
              </svg>
            </a>
            <a href="https://www.dropbox.com/home" target="_blank" rel="noopener noreferrer" title="Upload via Dropbox" className="hover:scale-110 transition-transform">
              {/* Dropbox SVG */}
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="8" fill="#0061FF"/>
                <polygon points="10,12 16,8 22,12 16,16" fill="#fff"/>
                <polygon points="10,20 16,16 22,20 16,24" fill="#fff"/>
              </svg>
            </a>
            <a href="https://onedrive.live.com/" target="_blank" rel="noopener noreferrer" title="Upload via OneDrive" className="hover:scale-110 transition-transform">
              {/* OneDrive SVG */}
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect width="32" height="32" rx="8" fill="#0078D4"/>
                <ellipse cx="16" cy="20" rx="8" ry="4" fill="#fff"/>
                <ellipse cx="16" cy="16" rx="6" ry="3" fill="#C7E0F4"/>
              </svg>
            </a>
          </div>
          <input type="url" name="fileUrl" id="fileUrl" placeholder="https://wetransfer.com/..." value={formData.fileUrl} onChange={handleChange} className="input w-full" />
          {fileError && <p className="text-red-500 text-sm mt-1">{fileError}</p>}
          <p className="text-xs text-gray-500 mt-1">Plaats hier een downloadlink van WeTransfer, Google Drive, Dropbox, etc. (optioneel)</p>
        </div>
        <div>
          <button type="submit" className="w-full btn-primary" disabled={loading}>
            {loading ? 'Versturen...' : 'Verstuur Bericht'}
          </button>
        </div>
        {success && <p className="text-green-600 font-semibold">Bedankt voor je bericht! We nemen zo snel mogelijk contact op.</p>}
        {error && <p className="text-red-600 font-semibold">{error}</p>}
      </form>
    </>
  );
} 